<template>
  <div class="mt-2 flex w-[352px]">
    <OfferForm
      :offered-symbol="offer"
      :order-type="orderType"
      :loading="
        isLoadingCreateInitialPublicOffer ||
        isLoadingPreCreateInitialPublicOffer
      "
      :type="FORM_TYPES.buy"
      @submit="handleSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useDialog, useToast } from '@negar/core';
import {
  useCreateInitialPublicOffer,
  useCreatePreInitialPublicOffer,
  FORM_TYPES,
  IPO_ORDER_TYPE,
  IPO_QUERY_KEYS,
} from '@core/ipo';
import { InitialPublicOfferItem } from '@libs/api-reference';
import { useQueryClient } from '@libs/use';

import { OfferForm } from '@/modules/ipo';

import useWebEngage from '@/composables/useWebEngage';

const props = defineProps<{
  isin: string;
  orderType: string;
  offer: InitialPublicOfferItem;
}>();

const { t } = useI18n();
const toast = useToast();
const { removeAll } = useDialog();

const queryClient = useQueryClient();

const { webEngage, onWebEngageReady } = useWebEngage();

const {
  createInitialPublicOffer,
  isPending: isLoadingCreateInitialPublicOffer,
} = useCreateInitialPublicOffer({
  onSuccess() {
    toast.neutral({
      message: t('ipo.createOrderMessage'),
      duration: 3000,
    });

    queryClient.invalidateQueries(IPO_QUERY_KEYS.ipo);

    removeAll();
  },
  onReject(error) {
    const message = error.response?.data[0].message;

    if (message) {
      toast.negative({
        message,
        duration: 3000,
      });
    } else {
      toast.negative({
        message: t('modules.order.error_creating_order_message'),
        duration: 3000,
      });
    }
  },
});

const {
  createPreInitialPublicOffer,
  isPending: isLoadingPreCreateInitialPublicOffer,
} = useCreatePreInitialPublicOffer({
  onSuccess() {
    toast.neutral({
      message: t('ipo.preOrderSubmit', {
        symbol: props.offer?.instrumentTitle,
      }),
      duration: 3000,
    });

    queryClient.invalidateQueries(IPO_QUERY_KEYS.ipo);

    removeAll();

    onWebEngageReady(() => {
      webEngage.track('Ipo_Pre_Order', { Namad: props.offer.instrumentTitle });
    });
  },
  onReject(error) {
    const message = error.response?.data[0].message;

    if (message) {
      toast.negative({
        message,
        duration: 3000,
      });
    } else {
      toast.negative({
        message: t('modules.order.error_creating_order_message'),
        duration: 3000,
      });
    }
  },
});

const handleSubmit = ({ instrumentIsin, price, quantity }, resetFormModel) => {
  if (
    props.orderType === IPO_ORDER_TYPE.preOrder ||
    props.orderType === IPO_ORDER_TYPE.preOrderNoPrice
  ) {
    createPreInitialPublicOffer(
      { instrumentIsin, price, quantity },
      resetFormModel
    );
  } else {
    createInitialPublicOffer(
      { instrumentIsin, price, quantity },
      resetFormModel
    );
  }
};
</script>
